import React, { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import {
  Outlet,
  useLocation,
  useNavigate,
  useOutletContext,
} from "react-router";
import BoxLayout from "../../../components/Common/BoxLayout/BoxLayout";
import Input from "../../../components/Common/Input/Input";
import DownloadIconBig from "../../../components/Common/SvgIcons/CommonIcons/DownloadIconBig";
import DownloadIconSmall from "../../../components/Common/SvgIcons/CommonIcons/DownloadIconSmall";
import Loader from "../../../components/Common/Loader/Loader";
import loaders from "../../../store/actions/actionCreators/loadersActionCreator";
import { postCSVBulkUploadActionHandler } from "../../../store/actions/actionHandlers/manageActionHandler";
import { getLocationListCSVActionHandler, getUserDepartmentListCSVActionHandler } from "../../../store/actions/actionHandlers/adminActionHandler/organizationActionHandler";
import { getWorkOrOfficeLocationListActionHandler } from "../../../store/actions/actionHandlers/distributeActionHandler";
import { getProductCategoryListActionHandler } from "../../../store/actions/actionHandlers/adminActionHandler/userManagementActionHandler";
import { organizationSettings } from "../../../utils/mainTabs/admin/organizationSettings/organizationSettings";
import { createTrainingType } from "../../../utils/mainTabs/manage/createTrainingType";
import classes from "./OrganizationSettings.module.scss";
import { FormattedMessage } from "react-intl";
import { setErrorHandler } from "../../../store/actions/actionHandlers/errorActionHandler";
import { errorTypes } from "../../../utils/errorTypes";
import { setUpDomainName } from "../../../utils/mainTabs/admin/organizationSettings/setUpDomainName";

const OrganizationSettings = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { tenantName, setAdminNavLinks, setAdminTiles, setAdminButtons } =
    useOutletContext();

  const [heading, setHeading] = useState("");
  const [sampleLink, setSampleLink] = useState("");
  const [fileTypeFlag, setFileTypeFlag] = useState(0);
  const [fileName, setFileName] = useState("");
  const [downloadLocationLoader, setDownloadLocationLoader] = useState(false);
  const [downloadUserDepartmentLoader, setDownloadUserDepartmentLoader] = useState(false);
  // Default to enabled. Will be set to false only after verifying no locations exist.
  const [hasLocations, setHasLocations] = useState(true);
  const [hasUserDepartments, setHasUserDepartments] = useState(true);

  const methods = useForm({
    defaultValues: { OrganizationSettingsCSVUpload: {} },
  });
  const { reset } = methods;

  const adminStateReducer = useSelector((state) => state.adminReducer);
  const { organizationSettingsReducer } = adminStateReducer;
  const { addCSVUploadLoader } = organizationSettingsReducer;

  const commonState = useSelector((state) => state.commonReducer);
  const { colors } = commonState;

  const handleDownloadUploadedLocations = async () => {
    setDownloadLocationLoader(true);
    await dispatch(getLocationListCSVActionHandler());
    setDownloadLocationLoader(false);
  };

  const handleDownloadUploadedUserDepartments = async () => {
    setDownloadUserDepartmentLoader(true);
    await dispatch(getUserDepartmentListCSVActionHandler());
    setDownloadUserDepartmentLoader(false);
  };

  // Check if locations exist for the tenant
  useEffect(() => {
    if (location.pathname === organizationSettings.location.path(tenantName)) {
      dispatch(getWorkOrOfficeLocationListActionHandler()).then((locations) => {
          // Only disable the download button when we confirm there are no locations.
          if (!locations || locations.length === 0) {
            setHasLocations(false);
          }
        });
    }
    if (location.pathname === organizationSettings.userDepartment.path(tenantName)) {
      dispatch(getProductCategoryListActionHandler()).then((data) => {
        // Only disable the download button when we confirm there are no user-departments.
        if (!data || data.length === 0) {
          setHasUserDepartments(false);
        }
      });
    }
  }, [location.pathname, tenantName, dispatch]);

  useEffect(() => {
    const {
      path,
      tiles: organizationSettingsTiles,
      navLinks: organizationSettingsNavLinks,
    } = organizationSettings;
    const organizationSettingsPath = path(tenantName);
    const newOrganizationSettingsTile = organizationSettingsTiles(
      navigate,
      tenantName
    );
    const newOrganizationSettingNavLinks = organizationSettingsNavLinks(
      navigate,
      tenantName
    );

    const {
      path: createTrainingTypePath,
      navLinks: createTrainingTypeNavLinks,
    } = createTrainingType;

    const createTrainingTypePathUpdate = createTrainingTypePath(tenantName);
    const createTrainingTypeNavLinksUpdate = createTrainingTypeNavLinks(
      navigate,
      tenantName
    );

    const { path: setUpDomainPath, navLinks: setUpDomainPathNavLinks } =
      setUpDomainName;
    const setUpDomainPathUpdate = setUpDomainPath(tenantName);
    const setUpDomainPathNavLinksUpdate = setUpDomainPathNavLinks(
      navigate,
      tenantName
    );

    if (location.pathname === organizationSettingsPath) {
      navigate(organizationSettings.location.path(tenantName));
    } else if (location.pathname === createTrainingTypePathUpdate) {
      setAdminButtons([]);
      setAdminNavLinks(createTrainingTypeNavLinksUpdate);
      setAdminTiles([]);
    } else if (location.pathname === setUpDomainPathUpdate) {
      setAdminButtons([]);
      setAdminNavLinks(setUpDomainPathNavLinksUpdate);
      setAdminTiles([]);
    } else if (location.pathname.includes(organizationSettingsPath)) {
      newOrganizationSettingsTile.forEach((tile) => {
        if (location.pathname === tile?.path) {
          const resolvedDownloadLink =
            typeof tile?.downloadLink === "function"
              ? tile.downloadLink()
              : tile?.downloadLink;
          const resolvedFileTypeFlag =
            typeof tile?.fileTypeFlag === "function"
              ? tile.fileTypeFlag()
              : tile?.fileTypeFlag;

          setHeading(tile?.heading);
          setSampleLink(resolvedDownloadLink);
          setFileTypeFlag(resolvedFileTypeFlag);
          setFileName(extractFileName(resolvedDownloadLink));
        }
      });
      setAdminButtons([]);
      setAdminNavLinks(newOrganizationSettingNavLinks);
      setAdminTiles(newOrganizationSettingsTile);
    }
  }, [
    tenantName,
    location,
    navigate,
    setAdminButtons,
    setAdminNavLinks,
    setAdminTiles,
  ]);

  const extractFileName = (downloadLink) => {
    if (!downloadLink) return '';
    const parts = downloadLink.split('/');
    const fileNameWithHash = parts.pop();
    const [fileName] = fileNameWithHash.split('.');
    return `${fileName}.csv`;
  };

  const handleCSVFileUpload = (e) => {
    let file = e.target.files[0];
    const allowedTypes = ["text/csv"];
    if (!allowedTypes.includes(file?.type)) {
      dispatch(setErrorHandler(errorTypes.FAIL, "Only CSV file are allowed"));
    } else if (file) {
      dispatch(loaders.admin.organizationSettings.addCSVUploadLoader(true));
      dispatch(postCSVBulkUploadActionHandler(fileTypeFlag, file)).then(
        (isUploaded) => {
          if (isUploaded) {
            dispatch(
              loaders.admin.organizationSettings.addCSVUploadLoader(false)
            );
          }
        }
      );
    }
    reset();
  };

  return location.pathname ===
    organizationSettings.createTrainingType.path(tenantName) ? (
    <Outlet />
  ) : location.pathname ===
    organizationSettings.setUpDomainName.path(tenantName) ? (
    <Outlet />
  ) : (
    <BoxLayout margin={[0, 0, 0, 0]} minHeight={42}>
      <div className={classes.organizationSettings}>
        <div className={classes.organizationSettings__heading}>
          <span>{heading}</span>
          {location.pathname ===
            organizationSettings.location.path(tenantName) && (
            <div
              className={`${classes.organizationSettings__heading__downloadBtn} ${!hasLocations ? classes.organizationSettings__heading__downloadBtn__disabled : ''}`}
              onClick={hasLocations ? handleDownloadUploadedLocations : undefined}
            >
              {downloadLocationLoader ? (
                <Loader small />
              ) : (
                <>
                  <DownloadIconSmall />
                  <span>
                    <FormattedMessage id="DOWNLOAD_UPLOADED_LOCATIONS" />
                  </span>
                </>
              )}
            </div>
          )}
          {location.pathname ===
            organizationSettings.userDepartment.path(tenantName) && (
            <div
              className={`${classes.organizationSettings__heading__downloadBtn} ${!hasUserDepartments ? classes.organizationSettings__heading__downloadBtn__disabled : ''}`}
              onClick={hasUserDepartments ? handleDownloadUploadedUserDepartments : undefined}
            >
              {downloadUserDepartmentLoader ? (
                <Loader small />
              ) : (
                <>
                  <DownloadIconSmall />
                  <span>
                    <FormattedMessage id="DOWNLOAD_USER_DEPARTMENT_LIST" />
                  </span>
                </>
              )}
            </div>
          )}
        </div>
        <div className={classes.organizationSettings__body}>
          {location.pathname ===
            organizationSettings.passwordExpiration.path(tenantName) ||
          location.pathname ===
            organizationSettings.addUserForSupport.path(tenantName) ? (
            <Outlet />
          ) : (
            <div
              className={classes.organizationSettings__body__uploadContainer}
            >
              <a
                href={sampleLink}
                download={fileName}
                className={
                  classes.organizationSettings__body__uploadContainer__download
                }
              >
                <div
                  className={
                    classes.organizationSettings__body__uploadContainer__download__downloadIcon
                  }
                >
                  <DownloadIconBig />
                </div>
                <FormattedMessage id="BULKUPLD_CSVFILEDOWNLOAD" />
              </a>
              <FormProvider {...methods}>
                <Input
                  name="OrganizationSettingsCSVUpload"
                  type="file"
                  hidden={true}
                  onChange={handleCSVFileUpload}
                  placeholder={<FormattedMessage id="UPLOAD_TEXT" />}
                  isLight={false}
                  accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                  loading={addCSVUploadLoader}
                />
              </FormProvider>
              <div>
                <FormattedMessage id="CSV_UPLOAD_NOTE" />
              </div>
            </div>
          )}
        </div>
      </div>
    </BoxLayout>
  );
};

export default OrganizationSettings;
